import type { GridApiPremium } from '@mui/x-data-grid-premium';
import type { RefObject } from 'react';
import type { Preference } from 'sr-types/lib/search/v1/graphql';
import { GRID_SETTINGS_GROUPING_COLUMN_FIELD } from './DataGrid/gridSettings/constants';
import { parseGridSettingsPreference } from './DataGrid/gridSettings/data/utils';
import type { GridSettingsPreference } from './DataGrid/gridSettings/types';
import { COLUMN_VIEW_SCHEMA_VERSION, type ColumnViewPreferenceValue, type SavedColumnView } from './columnViewTypes';

/** Serializes a column view preference value to a string. */
export function serializeColumnViewPreferenceValue(
    columnVisibilityModel: ColumnViewPreferenceValue['columnVisibilityModel'],
    orderedFields: string[],
    gridSettings?: GridSettingsPreference
): string {
    const value: ColumnViewPreferenceValue = {
        schemaVersion: COLUMN_VIEW_SCHEMA_VERSION,
        columnVisibilityModel: columnVisibilityModel ?? {},
        orderedFields: orderedFields ?? [],
        ...(gridSettings ? { gridSettings } : {})
    };
    return JSON.stringify(value);
}

/** Parses a column view preference value from a string. */
export function parseColumnViewPreferenceValue(value: unknown): ColumnViewPreferenceValue | null {
    let parsed = value;
    if (typeof value === 'string') {
        try {
            parsed = JSON.parse(value);
        } catch {
            return null;
        }
    }

    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return null;
    }

    const record = parsed as Record<string, unknown>;
    const model = record.columnVisibilityModel;
    const orderedFields = record.orderedFields;

    if (
        record.schemaVersion !== COLUMN_VIEW_SCHEMA_VERSION ||
        !model ||
        typeof model !== 'object' ||
        Array.isArray(model)
    ) {
        return null;
    }
    if (!Array.isArray(orderedFields) || orderedFields.some((field) => typeof field !== 'string')) {
        return null;
    }

    const gridSettings = parseGridSettingsPreference(record.gridSettings);

    return {
        schemaVersion: COLUMN_VIEW_SCHEMA_VERSION,
        columnVisibilityModel: model as ColumnViewPreferenceValue['columnVisibilityModel'],
        orderedFields,
        ...(gridSettings ? { gridSettings } : {})
    };
}

/** Maps a preference to a saved column view. */
export function mapPreferenceToSavedColumnView(preference: Preference): SavedColumnView | null {
    if (!preference?.id || !preference.name) {
        return null;
    }

    const parsed = parseColumnViewPreferenceValue(preference.value);
    if (!parsed) {
        return null;
    }

    return {
        id: preference.id,
        name: preference.name,
        description: preference.summary?.about ?? '',
        shared: Boolean(preference.shared),
        accountId: preference.accountId ?? undefined,
        security: preference.security,
        columnVisibilityModel: parsed.columnVisibilityModel,
        orderedFields: parsed.orderedFields,
        ...(parsed.gridSettings ? { gridSettings: parsed.gridSettings } : {})
    };
}

/**
 * Combines a saved/desired column order with what the grid currently has.
 *
 * Example:
 *   preferred (from a saved view): ['name', 'quantity', 'rateType']
 *   current (from MUI API):        ['__detail__', 'name', 'rateType', 'quantity', 'actions']
 *   result:                        ['name', 'quantity', 'rateType', '__detail__', 'actions']
 *
 * Steps:
 * 1. Keep fields from `preferred` that still exist in the grid, in preferred order.
 * 2. Append any grid fields not in preferred (system cols like actions/detail),
 *    in their current relative order.
 * 3. Put the row-grouping column back at the index it already has. It is absent from
 *    saved orders, and appending it would move grouping from the left to the far right
 *    when grouping is turned back on.
 *
 * Why: a saved view often omits MUI chrome columns. Applying preferred alone via
 * setColumnIndex would push those missing columns to the far right and scramble the layout.
 */
export function mergeOrderedFields(preferred: string[] | undefined, current: string[] | undefined): string[] {
    const preferredFields = preferred ?? [];
    const currentFields = current ?? [];
    if (!preferredFields.length) {
        return currentFields;
    }
    if (!currentFields.length) {
        return preferredFields;
    }

    const currentSet = new Set(currentFields);
    const preferredSet = new Set(preferredFields);
    const merged = preferredFields.filter((field) => currentSet.has(field));
    currentFields.forEach((field) => {
        if (!preferredSet.has(field)) {
            merged.push(field);
        }
    });
    return pinGroupingColumn(merged, currentFields);
}

function pinGroupingColumn(merged: string[], currentFields: string[]): string[] {
    const groupingIndex = currentFields.indexOf(GRID_SETTINGS_GROUPING_COLUMN_FIELD);
    if (groupingIndex === -1) {
        return merged;
    }

    const withoutGrouping = merged.filter((field) => field !== GRID_SETTINGS_GROUPING_COLUMN_FIELD);
    const insertAt = Math.min(groupingIndex, withoutGrouping.length);
    withoutGrouping.splice(insertAt, 0, GRID_SETTINGS_GROUPING_COLUMN_FIELD);
    return withoutGrouping;
}

/**
 * Builds a "back to default" column order when leaving a saved view.
 *
 * Unlike `mergeOrderedFields` (preferred list first, extras at the end), this keeps every
 * non-data column where it already is (detail toggle, row-grouping, actions, …) and only
 * reshuffles the data columns to match `columnDefFields`.
 *
 * Example (qty/rate were swapped on a saved view; leaving the view):
 *   columnDefFields:  ['icons', 'name', 'rateType', 'quantity']
 *   currentApiFields: ['__detail__', 'icons', '__row_group__', 'name', 'quantity', 'rateType', 'actions']
 *   result:           ['__detail__', 'icons', '__row_group__', 'name', 'rateType', 'quantity', 'actions']
 *                     └─ chrome unchanged ─┘                 └─ data cols back to def order ─┘
 *
 */
export function buildDefaultColumnOrder(
    columnDefFields: string[] | undefined,
    currentApiFields: string[] | undefined
): string[] {
    const defFields = columnDefFields ?? [];
    const currentFields = currentApiFields ?? [];
    if (!defFields.length) {
        return [...currentFields];
    }
    if (!currentFields.length) {
        return [...defFields];
    }

    const currentSet = new Set(currentFields);
    const defSet = new Set(defFields);
    const desiredDefOrder = defFields.filter((field) => currentSet.has(field));
    let nextDef = 0;

    return currentFields.map((field) => {
        if (defSet.has(field)) {
            return desiredDefOrder[nextDef++];
        }
        return field;
    });
}

/**
 * Moves columns so their left-to-right order matches `orderedFields`.
 * Unknown fields are skipped; columns missing from the list keep their relative position at the end.
 */
export function applyOrderedFields(apiRef: RefObject<GridApiPremium | null>, orderedFields: string[] | undefined) {
    const api = apiRef.current;
    if (!api || !orderedFields?.length || typeof api.setColumnIndex !== 'function') {
        return;
    }

    const currentFields = api.getAllColumns().map((column) => column.field);
    const fieldsToApply = mergeOrderedFields(orderedFields, currentFields);

    fieldsToApply.forEach((field, targetIndex) => {
        const fromIndex = api
            .getAllColumns()
            .map((column) => column.field)
            .indexOf(field);
        if (fromIndex === -1 || fromIndex === targetIndex) {
            return;
        }
        api.setColumnIndex(field, targetIndex);
    });
}

export function getOrderedFieldsFromApi(apiRef: RefObject<GridApiPremium | null>): string[] {
    const api = apiRef.current;
    if (!api || typeof api.getAllColumns !== 'function') {
        return [];
    }
    return api.getAllColumns().map((column) => column.field);
}

/** True when the relative order of column-def fields in `order` matches `defFields`. */
export function isColumnDefFieldOrder(order: string[] | undefined, defFields: string[] | undefined): boolean {
    const current = order ?? [];
    const defs = defFields ?? [];
    if (!defs.length || !current.length) {
        return false;
    }
    const defSet = new Set(defs);
    const defFieldsInOrder = current.filter((field) => defSet.has(field));
    const expected = defs.filter((field) => current.includes(field));
    return areOrderedFieldsEqual(defFieldsInOrder, expected);
}

/** Column order used for dirty checks. The MUI grouping column is not a user reorder. */
export function orderedFieldsWithoutGroupingColumn(fields: string[] | undefined): string[] {
    return (fields ?? []).filter((field) => field !== GRID_SETTINGS_GROUPING_COLUMN_FIELD);
}

export type GridSettingsDirtyTracking = {
    preset: number;
    savedViewId: string | null;
    revision: number;
    dirty: boolean;
};

/**
 * Sort/group edits belong to the column view that was selected when they were made.
 * Changing preset or saved view clears that dirty flag, even if the applied settings
 * have not caught up yet. A revision bump on the same view is dirty only when the
 * settings actually differ from that view.
 */
export function nextGridSettingsDirtyTracking(
    previous: GridSettingsDirtyTracking,
    next: {
        preset: number;
        savedViewId: string | null;
        revision: number;
        settingsDiffer: boolean;
    }
): GridSettingsDirtyTracking {
    const selectionChanged = previous.preset !== next.preset || previous.savedViewId !== next.savedViewId;
    const revisionChanged = previous.revision !== next.revision;
    const dirty = selectionChanged ? false : revisionChanged ? next.settingsDiffer : previous.dirty;

    return {
        preset: next.preset,
        savedViewId: next.savedViewId,
        revision: next.revision,
        dirty
    };
}

/** Exact compare of two column-order arrays (same fields, same positions). */
export function areOrderedFieldsEqual(a: string[] | undefined, b: string[] | undefined): boolean {
    const left = a ?? [];
    const right = b ?? [];
    if (left.length !== right.length) {
        return false;
    }
    return left.every((field, index) => field === right[index]);
}

/**
 * Compare visibility models for dirty checks. Missing keys are treated as visible (MUI default).
 */
export function areVisibilityModelsEqual(
    a: Record<string, boolean> | undefined,
    b: Record<string, boolean> | undefined
): boolean {
    const left = a ?? {};
    const right = b ?? {};
    const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
    for (const key of keys) {
        const leftVisible = left[key] !== false;
        const rightVisible = right[key] !== false;
        if (leftVisible !== rightVisible) {
            return false;
        }
    }
    return true;
}
